// frontend/src/pages/StoreDataHealth.jsx
// Staff-only: the Data Health summary of ANY store, read-only.
//
// Master-detail like Store Activity: the shared store rail on the left (or
// above, on a narrow screen), the selected store beside it, read as "what is wrong with this store's
// data": the problems that need attention first, each with what it affects
// and why it matters, then the problems the score leaves out, then the
// checks that passed or had nothing to look at. Observation only — there
// are deliberately no fix or ignore affordances here; those belong to the
// merchant's own Data Health page (and the backend endpoint exposes none).
//
// Data: /api/analytics/insights/stores/ (picker, via StorePickerRail) and
//       /api/admin/data-health/summary/?store_id= (drill-down).

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Tooltip } from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import { ICON, EyeSlash, HeartStraight } from '../icons';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { useFormatters } from '../hooks/useFormatters';
import { channelName } from '../utils/channelName';
import { ISSUE_DEFS } from '../datahealth/registry';
import StorePickerRail from '../components/staff/StorePickerRail';
import { Panel } from './storeActivity/primitives';
import StoreIdentity from './storeActivity/StoreIdentity';
import '../styles/pages.css';
import '../styles/store-activity.css';

// Same tone vocabulary as the merchant page (registry.js), on the app's CSS
// palette rather than MUI theme tokens — this page renders plain sa-* markup.
const TONE_VARS = {
  critical: 'var(--color-error)',
  warning: 'var(--color-warning)',
  info: 'var(--color-info)',
};

function StoreDataHealth() {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();

  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // selectedId only ever goes null → id (the rail has no deselect), so no
  // clearing branch is needed — data stays null until the first pick.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await sessionApi.get(
          `/api/admin/data-health/summary/?store_id=${encodeURIComponent(selectedId)}`,
        );
        if (!res.ok) throw await apiErrorFrom(res, 'GET /api/admin/data-health/summary/');
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        console.error('storeDataHealth load:', e);
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedId]);

  return (
    <div className="page-container">
      <div className="sa-shell">
        <StorePickerRail selectedId={selectedId} onSelect={setSelectedId} />

        <main className="sa-detail">
          <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'storeDataHealth.loadFailed')]} />
          {loading && <div className="sa-empty"><CircularProgress size={28} /></div>}

          {!selectedId && !loading && !error && (
            <div className="sa-empty">
              <HeartStraight size={ICON.display} weight="thin" />
              <span className="sa-empty-title">{t('storeDataHealth.title')}</span>
              <span>{t('storeDataHealth.selectPrompt')}</span>
            </div>
          )}

          {data && !loading && (
            <Summary data={data} t={t} formatNumber={formatNumber} />
          )}
        </main>
      </div>
    </div>
  );
}

function Summary({ data, t, formatNumber }) {
  const { store, issues } = data;
  const channel = channelName(store.store_type, t);

  // Four groups, in reading order. `checked === 0` is a check with nothing
  // to look at (an ERP check on a store with no ERP) — not a pass.
  const attention = issues.filter((i) => i.needs_attention);
  const notCounted = issues.filter((i) => i.count > 0 && !i.needs_attention);
  const passed = issues.filter((i) => i.count === 0 && i.checked !== 0);
  const notApplicable = issues.filter((i) => i.count === 0 && i.checked === 0);

  const rowProps = { channel, t, formatNumber };

  return (
    <>
      <section className="sa-hero">
        <StoreIdentity store={store}>
          <span className={`sa-pill sa-pill--${store.is_active ? 'active' : 'churned'}`}>
            {store.is_active ? t('storeActivity.picker.active') : t('storeActivity.picker.churned')}
          </span>
        </StoreIdentity>
        {/* Store size, not problems: the denominators the rows below count
            against. */}
        <div className="sa-hero-meta">
          {store.organization && (
            <div className="sa-meta-item">
              <span className="sa-meta-label">{t('storeActivity.lifecycle.organization')}</span>
              <span className="sa-meta-value">{store.organization}</span>
            </div>
          )}
          <div className="sa-meta-item">
            <span className="sa-meta-label">{t('storeDataHealth.activeItems')}</span>
            <span className="sa-meta-value">{formatNumber(data.total_active_items, 0)}</span>
          </div>
          <div className="sa-meta-item">
            <span className="sa-meta-label">{t('storeDataHealth.suppliers')}</span>
            <span className="sa-meta-value">{formatNumber(data.supplier_count, 0)}</span>
          </div>
        </div>
      </section>

      <section className="sa-panel sa-verdict">
        <div className="sa-verdict-title">
          {data.needs_attention_count > 0
            ? t('storeDataHealth.verdict.issues', { count: data.needs_attention_count })
            : t('storeDataHealth.verdict.none')}
        </div>
        <div className="sa-verdict-detail">
          {t('storeDataHealth.verdict.items', {
            products: formatNumber(data.products_with_issues, 0),
            total: formatNumber(data.total_active_items, 0),
          })}
        </div>
      </section>

      <div className="sa-grid">
        {attention.length > 0 && (
          <Panel className="sa-panel--wide" title={t('storeDataHealth.sections.attention')}>
            <IssueList issues={attention} {...rowProps} explain />
          </Panel>
        )}
        {notCounted.length > 0 && (
          <Panel
            className="sa-panel--wide"
            title={t('storeDataHealth.sections.notCounted')}
            subtitle={t('storeDataHealth.sections.notCountedHint')}
          >
            <IssueList issues={notCounted} {...rowProps} explain />
          </Panel>
        )}
        {passed.length > 0 && (
          <Panel title={t('storeDataHealth.sections.passed')}>
            <IssueList issues={passed} {...rowProps} />
          </Panel>
        )}
        {notApplicable.length > 0 && (
          <Panel
            title={t('storeDataHealth.sections.notApplicable')}
            subtitle={t('storeDataHealth.sections.notApplicableHint')}
          >
            <IssueList issues={notApplicable} {...rowProps} />
          </Panel>
        )}
      </div>
    </>
  );
}

// One check per row: what it is, which part of the store it touched, and —
// with `explain`, for the rows that found something — why it matters.
function IssueList({ issues, channel, t, formatNumber, explain = false }) {
  return (
    <div className="sa-list">
      {issues.map(({ key: issueKey, ...issue }) => (
        <div className="sa-list-row" key={issueKey}>
          {/* Strict lookup, no neutral fallback: every summary key has an
              ISSUE_DEFS entry with a TONE_VARS tone — pinned by
              test_frontend_defs_cover_every_backend_issue. A fallback here
              would dress registry drift as an informational issue. */}
          <span
            className="sa-status-dot"
            style={{
              background: TONE_VARS[ISSUE_DEFS[issueKey].tone],
              opacity: issue.count === 0 ? 0.35 : 1,
            }}
          />
          <div className="sa-list-main">
            <div className="sa-list-primary">{t(`dataHealth.${issueKey}Title`, { channel })}</div>
            {explain && (
              <div className="sa-list-secondary">{t(`dataHealth.${issueKey}Benefit`, { channel })}</div>
            )}
          </div>
          {issue.ignored && (
            <Tooltip title={t('storeDataHealth.ignoredByMerchant')} describeChild>
              <span className="sa-chip sa-chip--system">
                <EyeSlash size={ICON.xs} style={{ verticalAlign: 'text-bottom' }} />
                {' '}
                {t('storeDataHealth.ignored')}
              </span>
            </Tooltip>
          )}
          {issue.auto_resolved && issue.count > 0 && (
            <span className="sa-chip sa-chip--system">{t('storeDataHealth.autoResolved')}</span>
          )}
          {/* Overselling only (null elsewhere): how many of those items are
              below zero now — the same second number the merchant's card
              shows, so the two pages read alike. */}
          {issue.currently_negative > 0 && (
            <span className="sa-chip sa-chip--system">
              {t('dataHealth.oversellingBelowZero', { count: issue.currently_negative })}
            </span>
          )}
          <span className="sa-list-value">{scopeLine(issue, t, formatNumber)}</span>
        </div>
      ))}
    </div>
  );
}

// "1 of 11 items" — offending rows out of the rows the check looked at, in
// the issue's own grain. A pair sweep has no row population to count
// against (`checked` is null), so it says how many pairs it found.
function scopeLine(issue, t, formatNumber) {
  if (issue.entity === 'pair') {
    return t('storeDataHealth.pairs', { count: issue.count, n: formatNumber(issue.count, 0) });
  }
  return t(`storeDataHealth.scope.${issue.entity}`, {
    count: issue.checked,
    offending: formatNumber(issue.count, 0),
    checked: formatNumber(issue.checked, 0),
  });
}

export default StoreDataHealth;
